import { TaggedEntity } from '@epicuro-next/data/tagged-entities';
import { Maybe, ReviewStatus } from 'graphql-api/baskets-api-interfaces';

import { BasketAccount, BasketAccountReference } from './account.dto';
import { ReferencedProduct } from './product.dto';

export interface DtoBasket {
  basketAccounts: BasketAccount[];
  referencedProducts: ReferencedProduct[];
  items: Item[];
  isReadOnly: boolean;
  basketId: string;
  status: string;
  clientReferenceNumber: string;
  orderReferenceNumber: string;
  propertyBag: BasketPropertyBag;
  userId: string;
  userDisplayName: string;
  validationResults: BasketValidationResultModel[];
  basketItemCategoryAttributeKey: string;
  allowUserToSpecifyShipOrderInFullForNowOrder: boolean;
  userSpecifiedGroupedDelivery: boolean;
  summary: DtoBasketSummary;
  checkOrderExpiryUtc?: string;
  submittedTime?: string;
  assignee?: IBasketUser;
  assigner?: IBasketUser;
  reviewNeeded?: boolean;
  reviewStatus?: ReviewStatus;
  outputUrls: {
    h2: string;
  };
}

export interface ExtendedDtoBasket extends DtoBasket, TaggedEntity {}

export interface Item {
  basketAccountId: string;
  brandId: string;
  basketItemOrigin: BasketItemOrigin;
  confirmedOrderNumber: string;
  confirmedQuantity?: number;
  consignmentEndDate?: string;
  consignmentStartDate?: string;
  createdDateTimeUtc: string;
  deliveryId: string;
  displayDateFrom: string;
  displayDateTo: string;
  expectedDeliveryDate: string;
  inventoryId?: string;
  itemId: string;
  lastUpdatedDateTimeUtc: string;
  note?: string;
  orderType: OrderType;
  prices: BasketItemPrice;
  productId: string;
  requestedDeliveryDate: string;
  requestedQuantity: number;
  validationResults: BasketValidationResultModel[];
}

export interface Price {
  unitPrice: number;
  price: number;
  currency: string;
}

export interface BasketItemPrice {
  discount?: number;
  discountedGrossPrice: Price;
  grossPrice: Price;
  netPrice: Price;
  retailPrice: Price;
}

export interface BasketValidationResultModel {
  severity: number;
  message?: string;
  translationKey?: string;
  values?: string[];
}

export type BasketItemOrigin = {
  originInformation: string;
  source: string;
};

export type ItemsMap = {
  [itemId: string]: Item;
};

export interface BasketPatchModel {
  basketHeader: BasketHeader;
  itemsToInsertOrUpdate: BasketPatchItem[];
  itemIdsToRemove: string[];
  etag?: string | null;
}

export interface BasketHeader {
  basketId: string;
  orderReferenceNumber?: string | null;
  clientReferenceNumber?: string | null;
  userSpecifiedGroupedDelivery?: boolean | null;
  basketAccounts: BasketAccountReference[];
  propertyBag?: BasketPropertyBag;
}

export interface BasketPatchItem {
  itemId?: string | null;
  brandId: string;
  productId: string;
  basketAccountId: string;
  originInformation?: Record<string, unknown> | null;
  requestedQuantity: number;
  requestedDeliveryDate?: string | null;
  displayDateFrom?: string | null;
  displayDateTo?: string | null;
  orderType?: OrderType;
  note?: string | null;
  consignmentStartDate?: string | null;
  consignmentEndDate?: string | null;
  source: {
    type: UpdateBasketItemSource | string;
    data?: string;
  };
}

export enum OrderType {
  Now = 1,
  Future = 2,
}

export interface IBasketUser {
  displayName: string;
  email: string;
  userId: string;
}

export interface IProductInfo {
  prepackMaterialIds?: string[];
  materialId?: string;
  articleId: string;
}

export interface BasketManagerResultModel {
  page: number;
  size: number;
  total: number;
  data: BasketManagerItemModel[];
}

export interface BasketManagerItemModel {
  basketId: string;
  orderReferenceNumber?: string;
  clientReferenceNumber?: string;
  status?: BasketStatusModel;
  lastUpdatedUtc?: string;
  checkOrderExpiryUtc?: string;
  createdBy?: BasketManagerUserModel;
  submittedBy?: BasketManagerUserModel;
  brands: BasketManagerBrandModel[];
  addresses: Maybe<BasketManagerAddressModel>[];
  quantity: number;
  value: number;
  rrp: number;
  rrpCurrency?: string;
  wsp: number;
  wspCurrency?: string;
  currency?: string;
  orderType?: {
    id: number;
    name: string;
  };
  hasDiscount: boolean;
  isMerged: boolean;
}

export interface BasketStatusModel {
  id: number;
  name: string;
}

export interface BasketManagerUserModel {
  id: number;
  displayName: string;
  userName: string;
  lastName: string;
  emailAddress: string;
  profilePictureUrl: string;
}

export interface BasketManagerBrandModel {
  brandId: string;
  name: string;
  logoUrl: string;
}

export interface BasketManagerAddressModel {
  accountId: string;
  domainId: string;
  externalAccountId: string;
  accountName: string;
  addressId: string;
  externalAddressId: string;
  addressName: string;
  street: string;
  box: string;
  zipCode: string;
  city: string;
  country: string;
  countryCode: string;
  state: string;
  stateCode: string;
  isDropship: boolean;
}

export interface DtoBasketSummary {
  totalPiecesQuantity: number;
}

export interface DTOMultiCurrencyPriceContract {
  [currency: string]: number;
}

export interface BasketReviewMessage {
  message: string;
  siteId: string;
  useUrlResolver?: boolean;
  subSiteId?: string | null;
}

export enum UpdateBasketItemSource {
  Scanner = 'scanner',
  AddRemoveShops = 'basket-add-remove-shops',
  Reschedule = 'basket-reschedule',
  CopyReschedule = 'basket-copy-reschedule',
  CopyShipTo = 'basket-copy-ship-to',
}

export type BasketPropertyBag = {
  shippingConditions?: string;
  orderReason?: string;
  parkingReason?: string;
  parkingDate?: string;
  mergedFromBaskets?: string;
} & {
  [key: string]: string;
};
